/**
 * Curated Mockaroo field-type catalog.
 *
 * Mockaroo supports 150+ field types; this catalog surfaces the ones useful
 * for 8x8 internal bulk-tool testing. The full reference lives at
 * https://www.mockaroo.com/docs.
 *
 * Each entry declares:
 *   - `type`: the exact string Mockaroo expects in the schema body
 *   - `label`: display name for the dropdown
 *   - `category`: groups for the dropdown's optgroup rendering
 *   - `options`: per-field configurable params, surfaced as inputs in the UI
 *
 * If a column needs a type not listed here, users can drop a free-form
 * value through the "Custom type" escape hatch in MockarooFieldSelect.
 */

export type MockarooCategory =
  | 'identity'
  | 'names'
  | 'contact'
  | 'location'
  | 'telecom'
  | 'business'
  | 'numbers'
  | 'datetime'
  | 'text'
  | 'other';

export interface MockarooOptionDef {
  name: string;
  label: string;
  control: 'string' | 'number' | 'boolean' | 'multiline';
  required?: boolean;
  default?: string | number | boolean;
  help?: string;
}

export interface MockarooFieldDef {
  type: string;
  label: string;
  category: MockarooCategory;
  description?: string;
  options?: MockarooOptionDef[];
}

export const MOCKAROO_FIELDS: MockarooFieldDef[] = [
  // ── Identity ────────────────────────────────────────────────────────
  { type: 'Row Number', label: 'Row Number', category: 'identity', description: '1, 2, 3, …' },
  { type: 'Sequence', label: 'Sequence', category: 'identity', description: 'Increasing integer with custom start/step',
    options: [
      { name: 'start', label: 'Start', control: 'number', default: 1 },
      { name: 'step', label: 'Step', control: 'number', default: 1 },
    ] },
  { type: 'GUID', label: 'GUID', category: 'identity', description: 'Random UUID v4' },
  { type: 'MongoDB ObjectId', label: 'MongoDB ObjectId', category: 'identity' },

  // ── Names ───────────────────────────────────────────────────────────
  { type: 'Full Name', label: 'Full Name', category: 'names' },
  { type: 'First Name', label: 'First Name', category: 'names' },
  { type: 'Last Name', label: 'Last Name', category: 'names' },
  { type: 'Username', label: 'Username', category: 'names' },

  // ── Contact ─────────────────────────────────────────────────────────
  { type: 'Email Address', label: 'Email Address', category: 'contact' },
  { type: 'URL', label: 'URL', category: 'contact' },
  { type: 'Domain Name', label: 'Domain Name', category: 'contact' },
  { type: 'IP Address v4', label: 'IPv4 Address', category: 'contact' },

  // ── Telecom (the meat of bulk-tool testing) ─────────────────────────
  { type: 'Phone', label: 'Phone (formatted)', category: 'telecom',
    description: 'Generic formatted phone like "(555) 123-4567"' },
  { type: 'Regular Expression', label: 'Regex → E.164 phone', category: 'telecom',
    description: 'Use the value field below to control format. Default = US E.164.',
    options: [
      { name: 'value', label: 'Pattern', control: 'string', default: '\\+1[2-9][0-9]{9}', required: true,
        help: 'Mockaroo regex syntax. Examples: US E.164 \\+1[2-9][0-9]{9}; UK \\+44[0-9]{10}' },
    ] },
  { type: 'Time Zone', label: 'Time Zone (IANA)', category: 'telecom',
    description: 'Returns IANA names like America/Los_Angeles' },
  { type: 'Language', label: 'Language', category: 'telecom' },

  // ── Location ────────────────────────────────────────────────────────
  { type: 'Country', label: 'Country', category: 'location' },
  { type: 'Country Code', label: 'Country Code (ISO-2)', category: 'location' },
  { type: 'State', label: 'State', category: 'location' },
  { type: 'City', label: 'City', category: 'location' },
  { type: 'Street Address', label: 'Street Address', category: 'location' },
  { type: 'Postal Code', label: 'Postal Code', category: 'location' },
  { type: 'Latitude', label: 'Latitude', category: 'location' },
  { type: 'Longitude', label: 'Longitude', category: 'location' },

  // ── Business ────────────────────────────────────────────────────────
  { type: 'Company Name', label: 'Company Name', category: 'business' },
  { type: 'Job Title', label: 'Job Title', category: 'business' },
  { type: 'Department', label: 'Department', category: 'business' },
  { type: 'Industry', label: 'Industry', category: 'business' },
  { type: 'Currency Code', label: 'Currency Code', category: 'business' },

  // ── Numbers ─────────────────────────────────────────────────────────
  { type: 'Number', label: 'Integer (range)', category: 'numbers',
    options: [
      { name: 'min', label: 'Min', control: 'number', default: 0, required: true },
      { name: 'max', label: 'Max', control: 'number', default: 100, required: true },
      { name: 'decimals', label: 'Decimals', control: 'number', default: 0 },
    ] },
  { type: 'Money', label: 'Money', category: 'numbers',
    options: [
      { name: 'min', label: 'Min', control: 'number', default: 0 },
      { name: 'max', label: 'Max', control: 'number', default: 10000 },
    ] },

  // ── Date/Time ───────────────────────────────────────────────────────
  { type: 'Date', label: 'Date', category: 'datetime',
    options: [
      { name: 'min', label: 'From (YYYY-MM-DD)', control: 'string', default: '2020-01-01' },
      { name: 'max', label: 'To (YYYY-MM-DD)', control: 'string', default: '2026-12-31' },
      { name: 'format', label: 'Format', control: 'string', default: '%Y-%m-%d', help: 'strftime tokens' },
    ] },
  { type: 'Time', label: 'Time', category: 'datetime' },
  { type: 'Datetime', label: 'Datetime', category: 'datetime' },

  // ── Text ────────────────────────────────────────────────────────────
  { type: 'Custom List', label: 'Custom List (pick one)', category: 'text',
    description: 'Comma-separated list of values to pick from',
    options: [
      { name: 'values', label: 'Values (one per line)', control: 'multiline', required: true,
        default: 'NA\nEU\nAPAC',
        help: 'One value per line. Each row picks one at random.' },
      { name: 'selectionStyle', label: 'Pick style', control: 'string', default: 'random',
        help: 'random | sequential | weighted' },
    ] },
  { type: 'Words', label: 'Words (lorem)', category: 'text',
    options: [
      { name: 'min', label: 'Min words', control: 'number', default: 1 },
      { name: 'max', label: 'Max words', control: 'number', default: 3 },
    ] },
  { type: 'Sentences', label: 'Sentences (lorem)', category: 'text',
    options: [
      { name: 'min', label: 'Min sentences', control: 'number', default: 1 },
      { name: 'max', label: 'Max sentences', control: 'number', default: 3 },
    ] },
  { type: 'Boolean', label: 'Boolean (true/false)', category: 'other' },

  // ── Other ───────────────────────────────────────────────────────────
  { type: 'Color', label: 'Color name', category: 'other' },
  { type: 'Hex Color', label: 'Hex Color', category: 'other' },
];

export const MOCKAROO_FIELDS_BY_CATEGORY: Record<MockarooCategory, MockarooFieldDef[]> =
  MOCKAROO_FIELDS.reduce((acc, f) => {
    (acc[f.category] ??= []).push(f);
    return acc;
  }, {} as Record<MockarooCategory, MockarooFieldDef[]>);

export const CATEGORY_LABELS: Record<MockarooCategory, string> = {
  identity: 'Identity',
  names: 'Names',
  contact: 'Contact / Internet',
  telecom: 'Telecom',
  location: 'Location',
  business: 'Business',
  numbers: 'Numbers',
  datetime: 'Date / Time',
  text: 'Text',
  other: 'Other',
};

export function findFieldDef(type: string): MockarooFieldDef | undefined {
  return MOCKAROO_FIELDS.find((f) => f.type === type);
}
